import { z } from "zod";
export const llmProviderValues = [
    "openai",
    "anthropic",
    "google",
    "together",
    "runpod",
    "verda",
    "modal",
    // The scripted model emulator of the browser suite's session checks (twin-qa/emulators/model).
    // The gateway serves it only where MLX_MODEL_EMULATOR_URL names one on the instance's network.
    "emulator",
];
/** The one model the scripted model emulator serves. */
export const MODEL_EMULATOR_MODEL = "scripted-v1";
export function normalizeLlmProviderValue(value) {
    if (typeof value !== "string")
        return value;
    const normalized = value.trim().toLowerCase();
    return normalized === "gemini" ? "google" : normalized;
}
export const llmProviderSchema = z.preprocess(normalizeLlmProviderValue, z.enum(llmProviderValues));
export const modelReasoningLevelSchema = z.preprocess((value) => {
    if (typeof value !== "string")
        return value;
    const normalized = value.trim().toLowerCase();
    if (normalized === "false" || normalized === "0")
        return "off";
    return normalized;
}, z.enum(["off", "minimal", "low", "medium", "high", "xhigh", "max"]));
export const modelSamplingParametersSchema = z.object({
    temperature: z.number().min(0).max(2).optional(),
    topP: z.number().gt(0).max(1).optional(),
    topK: z.number().int().min(0).max(1_000).optional(),
    minP: z.number().min(0).max(1).optional(),
    repetitionPenalty: z.number().min(0.1).max(2).optional(),
    seed: z.number().int().min(0).max(2_147_483_647).optional(),
}).strict().refine((value) => Object.values(value).some((item) => item !== undefined), {
    message: "at least one sampling parameter is required",
});
export const modelTargetConfigSchema = z
    .object({
    provider: llmProviderSchema.optional(),
    model: z.string().trim().min(1).max(200).optional(),
    reasoning: modelReasoningLevelSchema.optional(),
    maxTokens: z.number().int().positive().optional(),
    sampling: modelSamplingParametersSchema.optional(),
    /** Restricted operator evaluation; does not change provider/model identity. */
    evaluationHarness: z.enum(["electric", "venus-native"]).optional(),
})
    .refine((value) => value.provider || value.model || value.reasoning || value.maxTokens || value.sampling, {
    message: "provider, model, reasoning, maxTokens, or sampling is required",
});
export const VENUS_LIGHTNING_V2_VERSION = "venus-lightning-v2";
export const runModelConfigSchema = z
    .object({
    executor: modelTargetConfigSchema.optional(),
    harness: z.enum(["venus-lightning-v1", VENUS_LIGHTNING_V2_VERSION]).optional(),
    harnessProfile: z.enum(["compact-actions-instruct-v1", "compact-actions-adapter-v1"]).optional(),
})
    .refine((value) => value.executor, {
    message: "executor model config is required",
});
/**
 * Max: the model a member switches a session to for harder tasks, in place of the default. It is
 * offered, and a member's request for it admitted, only where the tenant publishes a credit rate for it.
 */
export const MAX_MODE_EXECUTOR = { provider: "anthropic", model: "claude-opus-5-5" };
export function isMaxModeModelConfig(config) {
    return config?.executor?.provider === MAX_MODE_EXECUTOR.provider &&
        config.executor.model === MAX_MODE_EXECUTOR.model;
}
export function normalizeLlmProvider(value) {
    const parsed = llmProviderSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}
